import { useState } from "react"
import { useApolloClient } from "@apollo/client"
import { toast } from "@/v2-ui/toast"
import { ConfirmButton } from "@/v2-backoffice-client/components/button"
import { useTranslationAiFillMutation } from "@/v2-backoffice-client/translation/catalog/translation.aiFill.mutation"
import { patchTranslationCatalogCache } from "@/v2-backoffice-client/translation/catalog/translation.catalog.cache"
import type { TranslationAiFillMutation } from "@/v2-backoffice-client/translation/catalog/__types__/translation.aiFill.mutation"
import type { TranslationRow } from "@/v2-backoffice-client/translation/translation.types"

type AiFillResult =
  TranslationAiFillMutation["aiFillMissingTranslations"][number]

// Rows per mutation call — must match the backend's maxAiFillItems cap. The
// backend packs ~10 rows per model call, so one chunk is ~4 AI calls.
const AI_FILL_CHUNK_SIZE = 40

export type TranslationAiFillActionProps = {
  selectedRows: TranslationRow[]
  onComplete?(): void
  // Flags the rows of the chunk currently being translated, so the table can
  // show a spinner on exactly those rows while the run works through them.
  onBusyRowsChange?(codes: string[], busy: boolean): void
}

// Selection-toolbar action for the catalog tables: AI-draft every missing
// language of the selected rows in one run, instead of opening the editor
// row by row. Existing text is never overwritten; new text is saved marked
// auto-translated for later human spot-review (the same contract as
// "Promote + AI translate").
function TranslationAiFillAction(props: TranslationAiFillActionProps) {
  const { selectedRows, onComplete, onBusyRowsChange } = props

  const [aiFill] = useTranslationAiFillMutation()
  const client = useApolloClient()
  const [progress, setProgress] = useState<{
    done: number
    total: number
  } | null>(null)

  // Complete rows have nothing to fill; skip them so the confirm copy and
  // the token spend reflect the real work.
  const rows = selectedRows.filter((row) => row.missingCount > 0)
  const missingTotal = rows.reduce((sum, row) => sum + row.missingCount, 0)

  // The saved texts land in the cached catalog as each chunk finishes — no
  // end-of-run refetch of the whole ~8k-row catalog, and the operator sees
  // rows complete progressively instead of the table sitting stale.
  function applyChunkToCache(chunk: TranslationRow[], results: AiFillResult[]) {
    const rowByCode = new Map(chunk.map((row) => [row.code, row]))
    patchTranslationCatalogCache(
      client.cache,
      results
        .filter((result) => result.Ok && result.Filled.length > 0)
        .flatMap((result) => {
          const row = rowByCode.get(result.Code)
          if (!row) return []
          return [
            {
              code: row.code,
              objectType: row.objectType,
              texts: result.Filled,
            },
          ]
        }),
    )
  }

  async function onConfirm() {
    const results: AiFillResult[] = []
    setProgress({ done: 0, total: rows.length })
    try {
      // Chunked sequential calls: each chunk's writes land before the next
      // starts, so a failure mid-run keeps everything already translated.
      for (let start = 0; start < rows.length; start += AI_FILL_CHUNK_SIZE) {
        const chunk = rows.slice(start, start + AI_FILL_CHUNK_SIZE)
        const chunkCodes = chunk.map((row) => row.code)
        onBusyRowsChange?.(chunkCodes, true)
        try {
          const res = await aiFill({
            variables: {
              input: {
                Items: chunk.map((row) => ({
                  Code: row.code,
                  ObjectType: row.objectType,
                  Texts: row.texts
                    .filter((text) => text.name !== "")
                    .map((text) => ({
                      LanguageCode: text.languageCode,
                      Name: text.name,
                      Description: text.description || null,
                    })),
                })),
              },
            },
          })
          const chunkResults = res.data?.aiFillMissingTranslations ?? []
          results.push(...chunkResults)
          applyChunkToCache(chunk, chunkResults)
        } finally {
          onBusyRowsChange?.(chunkCodes, false)
        }
        setProgress({
          done: Math.min(start + chunk.length, rows.length),
          total: rows.length,
        })
      }

      const failed = results.filter((result) => !result.Ok)
      const filled = results.reduce(
        (sum, result) => sum + result.FilledCount,
        0,
      )
      if (failed.length === 0) {
        toast({
          title: `AI-drafted ${filled} translation(s) across ${results.length} text(s) — marked auto-translated for review.`,
        })
      } else {
        toast({
          variant: "destructive",
          title: `AI-drafted ${filled} translation(s); ${failed.length} text(s) failed`,
          description: `${failed[0].Code}: ${failed[0].Error ?? "unknown error"}`,
        })
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: `AI translate stopped after ${results.length} of ${rows.length} text(s)`,
        description: error instanceof Error ? error.message : String(error),
      })
    }
    setProgress(null)
    onComplete?.()
  }

  return (
    <ConfirmButton
      variant="primary"
      isStacked
      iconSrc="magic-wand"
      disabled={progress !== null || rows.length === 0}
      isLoading={progress !== null}
      content="AI-draft every missing language of the selected rows (existing text is never touched). Saved marked auto-translated for later human review. ~10 rows share one AI call."
      confirmContent={`AI-translate ${missingTotal} missing language value(s) across ${rows.length} text(s)? Rows already complete are skipped.`}
      onConfirm={onConfirm}
      data-feature-code="UI_BackofficeTranslation_Catalog_AiFill"
    >
      {progress
        ? `Translating ${progress.done}/${progress.total}…`
        : "AI translate missing"}
    </ConfirmButton>
  )
}

export default TranslationAiFillAction
